import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Activity, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, Server, Database, Users, Zap } from 'lucide-react'
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { api } from '../../lib/api'

interface SystemMetrics {
  memory: {
    rss_mb: number
    percent: number
  }
  cpu: {
    percent: number
    num_threads: number
  }
  requests: {
    active: number
    total: number
  }
  system: {
    uptime_seconds: number
    open_files: number
    connections: number
  }
}

export function MonitoringDashboard() {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null)
  const [historicalData, setHistoricalData] = useState<any[]>([])
  const [healthScore, setHealthScore] = useState(95)

  useEffect(() => {
    // Fetch real metrics from health endpoint
    const fetchMetrics = async () => {
      try {
        const response = await api.get('/health')
        if (response.data && response.data.metrics) {
          setMetrics(response.data.metrics)
        }
      } catch (error) {
        console.error('Failed to fetch metrics:', error)
        // Fallback to mock data if API fails
        const mockMetrics: SystemMetrics = {
          memory: {
            rss_mb: 256,
            percent: 15
          },
          cpu: {
            percent: 20,
            num_threads: 8
          },
          requests: {
            active: 0,
            total: 0
          },
          system: {
            uptime_seconds: 0,
            open_files: 0,
            connections: 0
          }
        }
        setMetrics(mockMetrics)
      }

      // Update historical data
      if (metrics) {
        setHistoricalData(prev => {
          const newData = [
            ...prev,
            {
              time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
              cpu: metrics.cpu.percent,
              memory: metrics.memory.percent,
              requests: metrics.requests.active
            }
          ].slice(-20) // Keep last 20 data points
          return newData
        })
      }
    }

    fetchMetrics()
    const interval = setInterval(fetchMetrics, 5000) // Update every 5 seconds

    return () => clearInterval(interval)
  }, [])

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  const getHealthColor = (score: number) => {
    if (score >= 90) return 'text-green-500'
    if (score >= 70) return 'text-yellow-500'
    return 'text-red-500'
  }

  const getHealthIcon = (score: number) => {
    if (score >= 90) return <CheckCircle className="w-8 h-8 text-green-500" />
    if (score >= 70) return <AlertTriangle className="w-8 h-8 text-yellow-500" />
    return <AlertTriangle className="w-8 h-8 text-red-500" />
  }

  return (
    <div className="space-y-6">
      {/* Health Overview */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">System Health</h2>
          <div className="flex items-center space-x-4">
            {getHealthIcon(healthScore)}
            <div>
              <div className={`text-3xl font-bold ${getHealthColor(healthScore)}`}>
                {healthScore}%
              </div>
              <div className="text-sm text-gray-500">Health Score</div>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800/50 rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <Server className="w-5 h-5 text-blue-400" />
              <span className="text-xs text-gray-500">CPU</span>
            </div>
            <div className="text-2xl font-bold">{metrics?.cpu.percent.toFixed(1)}%</div>
            <div className="text-xs text-gray-400">{metrics?.cpu.num_threads} threads</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-800/50 rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <Database className="w-5 h-5 text-purple-400" />
              <span className="text-xs text-gray-500">Memory</span>
            </div>
            <div className="text-2xl font-bold">{metrics?.memory.rss_mb.toFixed(0)} MB</div>
            <div className="text-xs text-gray-400">{metrics?.memory.percent.toFixed(1)}% used</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gray-800/50 rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="text-xs text-gray-500">Requests</span>
            </div>
            <div className="text-2xl font-bold">{metrics?.requests.active}</div>
            <div className="text-xs text-gray-400">{metrics?.requests.total.toLocaleString()} total</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gray-800/50 rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-green-400" />
              <span className="text-xs text-gray-500">Uptime</span>
            </div>
            <div className="text-2xl font-bold">{formatUptime(metrics?.system.uptime_seconds || 0)}</div>
            <div className="text-xs text-gray-400">{metrics?.system.connections} connections</div>
          </motion.div>
        </div>
      </div>

      {/* Performance Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CPU & Memory Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="text-lg font-bold mb-4">System Resources</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                }}
              />
              <Line
                type="monotone"
                dataKey="cpu"
                stroke="#3B82F6"
                strokeWidth={2}
                name="CPU %"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="memory"
                stroke="#A78BFA"
                strokeWidth={2}
                name="Memory %"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Active Requests Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="text-lg font-bold mb-4">Active Requests</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                }}
              />
              <Area
                type="monotone"
                dataKey="requests"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Alerts & Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-xl p-6"
      >
        <h3 className="text-lg font-bold mb-4">System Insights</h3>
        <div className="space-y-3">
          {[
            {
              type: 'success',
              icon: <CheckCircle className="w-5 h-5 text-green-500" />,
              message: 'All systems operational',
              detail: 'No issues detected in the last 24 hours'
            },
            {
              type: 'warning',
              icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
              message: 'High memory usage detected',
              detail: 'Consider scaling up if usage remains high'
            },
            {
              type: 'info',
              icon: <TrendingUp className="w-5 h-5 text-blue-500" />,
              message: 'Request volume increased 23%',
              detail: 'Compared to last week'
            }
          ].map((alert, index) => (
            <div
              key={index}
              className={`flex items-start space-x-3 p-3 rounded-lg ${
                alert.type === 'warning' ? 'bg-yellow-500/10' : 'bg-gray-800/50'
              }`}
            >
              {alert.icon}
              <div className="flex-1">
                <p className="font-medium">{alert.message}</p>
                <p className="text-sm text-gray-400">{alert.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  )
}